import { randomHexColorWithArray } from 'random-hex-color-generator';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import useResponsiveRadius from '../../../../Hooks/useResponsiveRadius';
import { formatChartKeyToTitle } from '../../../../lib/utils/utils';

// ✅ Custom Tooltip for Aged Inventory
function AgedInventoryTooltip({ active, payload }) {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#121212] text-[#FFFFFF] rounded-lg p-2">
                {payload.map((entry, index) => (
                    <div
                        key={index}
                        className="flex items-center justify-between gap-2"
                    >
                        <div className="flex items-center gap-2">
                            <div
                                className="size-3 rounded"
                                style={{ backgroundColor: entry.payload.fill }}
                            ></div>
                            <span className="font-medium">
                                {formatChartKeyToTitle(entry.name)}
                            </span>
                        </div>

                        <span className="text-[#22C55E] font-semibold">
                            {entry.value}%
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
}

function AgedInventory() {
    const { productsInsights } = useSelector(
        (state) => state.reportAnalysis.productsInsights
    );

    const location = useLocation();

    const total = Object.entries(productsInsights.agedInventory).reduce(
        (sum, [key, value]) => sum + value,
        0
    );

    const [colors, setColors] = useState([]);
    const [mappedArray, setMappedArray] = useState([]);

    const containerRef = useRef();
    const radius = useResponsiveRadius(containerRef);

    useEffect(() => {
        const agedInventory = productsInsights.agedInventory;

        const colorsArray = randomHexColorWithArray(
            Object.keys(agedInventory).length
        );
        setColors(colorsArray);

        if (Object.keys(agedInventory).length > 0) {
            const arr = Object.entries(agedInventory).map(([key, value]) => ({
                name: key,
                value: value,
            }));

            setMappedArray(arr);
        }
    }, [productsInsights]);

    return (
        <div className="border rounded-lg border-[#EAEAEA] w-1/2 aspect-video p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h1 className="font-semibold text-base">Aged Inventory</h1>

                <Link
                    className="text-[#1055EB] font-semibold text-[12px] leading-6"
                    to={`${location.pathname}/aged-inventory`}
                >
                    View all data
                </Link>
            </div>

            {Object.keys(productsInsights).length > 0 ? (
                Object.keys(productsInsights.agedInventory).length > 0 ? (
                    <>
                        {total === 0 ? (
                            <div className="m-auto">
                                <h1 className="font-normal italic text-base text-[#666666]">
                                    No graph available
                                </h1>
                            </div>
                        ) : (
                            <ResponsiveContainer
                                ref={containerRef}
                                className="relative"
                                width="100%"
                                height="100%"
                            >
                                <PieChart>
                                    <Pie
                                        data={mappedArray}
                                        innerRadius={radius.inner}
                                        outerRadius={radius.outer}
                                        fill="#8884d8"
                                        paddingAngle={0}
                                        dataKey="value"
                                    >
                                        {Object.entries(
                                            productsInsights.agedInventory
                                        ).map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={colors[index % colors.length]}
                                            />
                                        ))}
                                    </Pie>

                                    {/* ✅ Tooltip */}
                                    <Tooltip content={<AgedInventoryTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}

                        <div className="grid grid-cols-2 grid-rows-2 gap-x-16 max-[1280px]:gap-x-12 max-[1152px]:gap-x-6 gap-y-4">
                            {Object.entries(productsInsights.agedInventory).map(
                                ([key, value], index) => {
                                    return (
                                        <div
                                            className="flex w-full gap-2 items-center justify-between"
                                            key={index}
                                        >
                                            <div className="flex gap-2 items-center">
                                                <div
                                                    className="rounded-[4px] size-3"
                                                    style={{
                                                        backgroundColor: colors[index],
                                                    }}
                                                ></div>

                                                <h2 className="text-[12px] leading-5 font-semibold text-[#666666]">
                                                    {key}
                                                </h2>
                                            </div>

                                            <h2 className="text-[12px] leading-5 font-semibold">
                                                {value}%
                                            </h2>
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    </>
                ) : (
                    <div className="m-auto">
                        <h1 className="font-normal italic text-base text-[#666666]">
                            No data available
                        </h1>
                    </div>
                )
            ) : (
                <div className="m-auto">
                    <h1 className="font-normal italic text-base text-[#666666]">
                        No data available
                    </h1>
                </div>
            )}
        </div>
    );
}

export default AgedInventory;
